import CardHeader from '@mui/material/CardHeader'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid2'
import { cvAPI } from 'api/CvAPI'
import { layoutAPI } from 'api/LayoutAPI'
import { organizationAPI } from 'api/OrganizationAPI'
import { personAPI } from 'api/PersonAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import { ToolbarItems } from 'Components/General/Toolbar/ToolbarItems'
import { getLayoutInitialData } from 'Components/reusable/DataContext/InitialData'
import { cvPrintLayoutSchema } from 'Components/reusable/DataContext/ValidationSchema'
import BackButton from 'Components/reusable/IconButtons/BackButton'
import colors from 'constants/colors'
import { useCountryData, useLanguageData, useUser } from 'hooks'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, Link, useParams } from 'react-router'
import { ICv } from 'types/cvsInterfaces'
import { IError } from 'types/error'
import { ILayout, IOrganizationColors } from 'types/layoutInterfaces'
import { IPerson } from 'types/userInterfaces'
import LayoutControls from './LayoutControls'
import LayoutModal from './LayoutModal'
import Preview from './Preview'
import PrintControls from './PrintControls'
import { usePreviewData } from './PrintPreviewProvider'
import LoadingIndicator from 'Components/reusable/LoadingIndicator'

/**
 * Resolves the image data from the given image source.
 *
 * @param {string} imageSrc - The source of the image.
 * @return {Promise<string>} A promise that resolves to the image data.
 */
export async function resolveImageData(imageSrc: string, signal?: AbortSignal): Promise<string> {
  if (imageSrc && imageSrc.startsWith('http')) {
    const blob = await fetch(imageSrc, signal ? { signal } : undefined)
      .then(r => r.blob())
      .catch(err => {
        if (signal?.aborted) return undefined
        console.error(err)
      })
    if (blob) {
      const reader = new FileReader()
      return new Promise((resolve, reject) => {
        reader.onload = () => {
          if (!reader.result) {
            reject('No result from reader')
            return
          }
          resolve(reader.result.toString())
        }
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    }
  }

  return imageSrc
}

/**
 * Generates the function comment for the given function body.
 *
 * @return {React.ReactElement} - The JSX element.
 * @notExported
 */
const CVPrint: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { user, features } = useUser()
  const [backendError, setBackendError] = useState<IError>()
  const [cvLayouts, setCvLayouts] = useState<ILayout[]>()
  const [loading, setLoading] = useState<boolean>()
  const [cvId, setCvId] = useState<number>()
  const [personId, setPersonId] = useState<number>()
  const [cvs, setCvs] = useState<ICv[]>()
  const [open, setOpen] = useState<boolean>(false)
  const [profileImage, setProfileImage] = useState<string>()
  const [orgLogo, setOrgLogo] = useState<string>()
  const [orgColors, setOrgColors] = useState<IOrganizationColors>({
    color: colors.primary,
    accentColor: colors.secondary,
  })
  const [ownCompanyBranding, setOwnCompanyBranding] = useState<boolean>(false)
  const [about, setAbout] = useState<IPerson>()
  const { countries } = useCountryData()
  const { languages } = useLanguageData()
  const { data, setData, visibleLayout, setVisibleLayout } = usePreviewData()
  const { cvId: matchCvId } = useParams()

  const getOrganizationLogoAndColors = useCallback(
    async (
      organizationId: number | undefined,
      organization?: { color?: string; accentColor?: string },
      controller?: AbortController
    ) => {
      if (!organizationId || !organization) return
      try {
        const imageSrc = await organizationAPI.getOrgLogo(organizationId, controller)
        const imageData = await resolveImageData(imageSrc)
        setOrgLogo(imageData)
        setOrgColors({
          color: organization.color ?? colors.primary,
          accentColor: organization.accentColor ?? colors.secondary,
        })
      } catch (error) {
        setBackendError(error as IError)
      }
    },
    []
  )

  const getLayouts = useCallback(async (cvId: number, controller?: AbortController) => {
    const layouts: ILayout[] = await layoutAPI.getAll(cvId, controller)
    layouts.unshift({
      id: 0,
      name: 'default',
      accountId: 0,
      cvId,
      ItemVisibilities: [],
      ItemOrders: [],
      showContactInfo: false,
      hideProfileImage: false,
      hideName: false,
      hideLastUsed: false,
      hideSkillExperience: false,
      showCertificateExpiry: false,
    })
    return layouts
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (matchCvId) {
        try {
          setLoading(true)
          const about: IPerson = await cvAPI.getCvAbout(parseInt(matchCvId, 10), controller)
          {
            setAbout(about)
            setPersonId(about.id)
            setCvId(parseInt(matchCvId, 10))
          }
        } catch (error) {
          setBackendError(error as IError)
        } finally {
          setLoading(false)
        }
      } else {
        setPersonId(user?.Person?.id)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [matchCvId, user?.Person?.id])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (personId) {
        try {
          setLoading(true)
          const personData = await personAPI.getPersonData(personId, controller)

          {
            if (personData && personData.CVs && personData.CVs.length > 0) {
              if (!matchCvId) {
                const defaultCV = personData.CVs.find((cv: ICv) =>
                  personData.defaultCvId ? cv.id === personData.defaultCvId : cv.original === true
                )
                if (defaultCV) {
                  setCvId(defaultCV.id)
                } else {
                  setCvId(personData.CVs[0].id)
                }
                const about: IPerson = await cvAPI.getCvAbout(
                  defaultCV ? defaultCV.id : personData.CVs[0].id,
                  controller
                )
                setAbout(about)
              }
              setCvs(personData.CVs)

              const imageSrc = await personAPI.getProfileImage(personId)
              if (imageSrc) {
                const imageData = await resolveImageData(imageSrc)
                setProfileImage(imageData)
              } else {
                setProfileImage('')
              }

              if (personData.Organization && personData.Organization.id) {
                await getOrganizationLogoAndColors(personData.Organization.id, personData.Organization, controller)
              } else {
                setOrgLogo('')
              }
            }
          }
        } catch (err) {
          setBackendError(err as IError)
        } finally {
          setLoading(false)
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [personId, matchCvId, getOrganizationLogoAndColors])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (cvId && about) {
        try {
          setLoading(true)
          const layouts: ILayout[] = await getLayouts(cvId, controller)
          if (layouts.length > 0) {
            setVisibleLayout(layouts?.find(layout => layout.id === 0))
            setCvLayouts(layouts)
          }
          const cv: ICv | undefined = await cvAPI.getCvData(cvId, controller)
          if (cv) {
            {
              const CvData = {
                about: {
                  cvId,
                  firstName: about.firstName,
                  lastName: about.lastName,
                  telephone: about.telephone,
                  email: about.Account?.email,
                  streetAddress: about.streetAddress,
                  postalCode: about.postalCode,
                  city: about.city,
                  country: about.country,
                  Organization: about.Site?.Organization,
                  translations: cv.translations,
                },
                degrees: cv.Education,
                courses: cv.Courses,
                personCertificates: cv.PersonCertificates,
                personCompetences: features.includes('competences') ? cv.PersonCompetences : [],
                employments: cv.Employers,
                leisureActivities: cv.LeisureActivities ?? [],
                projects: cv.Projects,
                references: cv.References,
                skills: cv.PersonSkills,
                industries: cv.Industries,
                roles: cv.Roles,
                languages: cv.LanguageSkills,
                urls: cv.Urls,
              }
              setData(CvData)
            }
          }
        } catch (err) {
          setBackendError(err as IError)
        } finally {
          setLoading(false)
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [about, cvId, getLayouts, setData, setVisibleLayout, features])

  useEffect(() => {
    const controller = new AbortController()

    if (visibleLayout) {
      ;(async () => {
        if (user && user.organizationId && ownCompanyBranding) {
          await getOrganizationLogoAndColors(user.organizationId, user.Organization, controller)
        } else {
          if (data?.about?.Organization?.id) {
            await getOrganizationLogoAndColors(data?.about?.Organization?.id, data?.about?.Organization, controller)
          }
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [data?.about?.Organization, getOrganizationLogoAndColors, ownCompanyBranding, visibleLayout, user])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  if (loading || !cvId) {
    return <LoadingIndicator />
  }

  return (
    <>
      <Grid
        container
        spacing={2}
        direction="row"
        justifyContent="center"
        alignItems="flex-start"
        style={{ padding: '34px', paddingBottom: '30px' }}
      >
        {personId && (
          <ToolbarItems>
            <Link to={user?.Person?.id === personId ? '/profile' : `/profile/${personId}`} state={location?.state}>
              <BackButton />
            </Link>
          </ToolbarItems>
        )}
        {cvLayouts && visibleLayout && (
          <Grid
            size={{
              xs: 12,
              md: 12,
              lg: 4,
            }}
          >
            <CardHeader title={t('print.layout.title')} />
            <PrintControls
              setOpen={setOpen}
              layouts={cvLayouts}
              setLayouts={setCvLayouts}
              setOwnCompanyBranding={setOwnCompanyBranding}
              ownCompanyBranding={ownCompanyBranding}
              organizationId={data?.about?.Organization?.id}
              setCv={setCvId}
              cvs={cvs}
              selectedId={cvId}
            />
            {visibleLayout.id === 0 ? (
              <Typography style={{ color: colors.gray }}>{t('printpreview')}</Typography>
            ) : data ? (
              <LayoutControls data={data} />
            ) : null}
          </Grid>
        )}
        {data && countries && languages && (
          <Grid
            size={{
              xs: 12,
              md: 12,
              lg: 8,
            }}
          >
            <Preview
              countries={countries}
              languages={languages}
              profileImage={profileImage}
              orgLogo={orgLogo}
              orgColors={orgColors}
              loading={loading}
            />
          </Grid>
        )}
      </Grid>
      {open && (
        <LayoutModal
          onClose={(value?: Record<string, unknown>) => {
            const newLayout = value as ILayout | undefined
            if (newLayout?.id) {
              setCvLayouts(layouts => {
                if (layouts) {
                  return [...layouts, newLayout]
                } else {
                  return [newLayout]
                }
              })
            }
            setOpen(false)
          }}
          cvId={cvId}
          initialData={getLayoutInitialData()}
          schema={cvPrintLayoutSchema()}
          localeBase="print.layout"
          hideLanguageHelperText={true}
          maxWidth="xs"
          fullWidth={true}
          submitOnModal={true}
          noEscClose
        />
      )}
    </>
  )
}

export default CVPrint
